import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, UserX, Eye, EyeOff, User, Link, UserPlus, UserCheck, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { ConfirmModal, PasswordConfirmModal } from '../ui';
import { ROUTES } from '../../api/routes';
import {
  InviteResultDialog,
  InviteStatusCard,
  getInviteStatus,
  copyCredentials,
  gerarSenha,
  normalizeActivationLink,
  type InviteUser,
} from '../invite';
import { UsuarioEditModal } from './UsuarioEditModal';
import { useAuthStore } from '../../store/authStore';

/* ---------------------------------------------------------------- */
/*  TYPES                                                            */
/* ---------------------------------------------------------------- */

interface ManagedUser extends InviteUser {
  especialidade?: string;
  cargo?: string;
  fotoUrl?: string;
  ativo?: boolean;
  convidadoPor?: string;
}

interface UserManagerTabProps {
  entityType: 'escola' | 'estado' | 'municipio';
  entityId: string;
  label: string;
  defaultPerfil?: string;
  defaultCargo?: string;
  filterCargo?: string;
}

/* ---------------------------------------------------------------- */
/*  COMPONENT                                                        */
/* ---------------------------------------------------------------- */

export function UserManagerTab({
  entityType,
  entityId,
  label,
  defaultPerfil,
  defaultCargo,
  filterCargo,
}: UserManagerTabProps) {
  const user = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [selectedUserForModal, setSelectedUserForModal] = useState<ManagedUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [resending, setResending] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState<ManagedUser | null>(null);
  const [newActivationLink, setNewActivationLink] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<ManagedUser | null>(null);

  const [form, setForm] = useState({ nome: '', email: '', senha: '', telefone: '' });
  const [credenciais, setCredenciais] = useState<{ email: string; senha?: string; activationLink?: string; whatsappLink?: string | null; perfil?: string; nome?: string; telefone?: string } | null>(null);

  const isInviteFlow = true;

  const CARGO_LABELS: Record<string, string> = {
    DIRETOR: 'Diretor',
    COORDENACAO: 'Coordenador',
    SECRETARIA: 'Secretária',
    PROFESSOR: 'Professor',
    FUNCIONARIO: 'Membro',
  };
  const cargoLabel = CARGO_LABELS[defaultCargo || filterCargo || ''] 
    || (defaultPerfil === 'MUNICIPAL' ? 'Gestor Municipal' : defaultPerfil === 'ESTADUAL' ? 'Gestor Estadual' : defaultPerfil === 'PROFESSOR' || label?.toLowerCase().includes('profess') ? 'Professor' : label.replace(/(?:es|s)$/i, ''));

  /* -- FETCH --------------------------------------------------- */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      if (entityType === 'escola') {
        const params = new URLSearchParams();
        if (filterCargo) params.set('cargo', filterCargo);
        if (defaultPerfil) params.set('perfil', defaultPerfil);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const { data } = await api.get(ROUTES.equipe.listar(entityId) + qs);
        setUsers(Array.isArray(data) ? data : []);
      } else {
        const { data } = await api.get(ROUTES.admin.usuarios);
        const list = Array.isArray(data) ? data : data.data || [];
        const filtered = list.filter((u: any) => {
          if (entityType === 'estado') return u.estadoId === entityId;
          if (entityType === 'municipio') return u.municipioId === entityId;
          return true;
        });
        setUsers(filtered);
      }
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, filterCargo, defaultPerfil]);

  useEffect(() => {
    fetchUsers();
    const handleRefetch = () => { fetchUsers(); };
    window.addEventListener('ac-data-refetch', handleRefetch);
    return () => window.removeEventListener('ac-data-refetch', handleRefetch);
  }, [fetchUsers]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const dateA = new Date((a as any).atualizadoEm || (a as any).criadoEm || 0).getTime();
      const dateB = new Date((b as any).atualizadoEm || (b as any).criadoEm || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      return (a.nome || '').localeCompare(b.nome || '');
    });
  }, [users]);

  /* -- FORMS --------------------------------------------------- */
  const openNew = () => {
    setEditingUser(null);
    setForm({ nome: '', email: '', senha: '', telefone: '' });
    setShowSenha(false);
    setCredenciais(null);
    setShowForm(true);
  };

  const openEdit = (u: ManagedUser) => {
    setSelectedUserForModal(u);
  };

  /* -- SAVE ---------------------------------------------------- */
  const handleSave = async () => {
    if (!form.nome.trim() || !form.email.trim()) { toast.error('Nome e Email sao obrigatorios'); return; }
    // Senha não é mais necessária — convite via link de ativação

    setSaving(true);
    try {
      if (editingUser) {
        if (entityType === 'escola') {
          await api.patch(ROUTES.equipe.findOne(entityId, editingUser.id), {
            nome: form.nome,
            ...(form.telefone ? { telefone: form.telefone } : {}),
          });
        } else {
          await api.put(`${ROUTES.admin.usuarios}/${editingUser.id}`, {
            nome: form.nome,
            email: form.email,
            ...(form.telefone ? { telefone: form.telefone } : {}),
          });
        }
        toast.success('Usuario atualizado!');
        setShowForm(false);
      } else {
        if (entityType === 'escola') {
          const perfil = defaultPerfil || (defaultCargo === 'PROFESSOR' || filterCargo === 'PROFESSOR' ? 'PROFESSOR' : 'ESCOLA');
          const cargo = defaultCargo || filterCargo || (perfil === 'PROFESSOR' ? 'PROFESSOR' : 'FUNCIONARIO');

          // Usar endpoint de convite (gera token + link de ativação)
          const { data: resp } = await api.post(ROUTES.equipe.convidar(entityId), {
            nome: form.nome,
            email: form.email,
            cargo,
            perfil,
            ...(form.telefone ? { telefone: form.telefone } : {}),
          });
          setCredenciais({
            email: form.email,
            nome: form.nome,
            telefone: form.telefone,
            perfil,
            activationLink: resp.activationLink || resp.linkAtivacao,
            whatsappLink: resp.whatsappLink || null,
          });
          toast.success(resp.message || 'Convite enviado!');
        } else {
          const perfil = defaultPerfil || (entityType === 'estado' ? 'ESTADUAL' : 'MUNICIPAL');
          const fkField = entityType === 'estado' ? 'estadoId' : 'municipioId';
          const { data: resp } = await api.post(ROUTES.admin.usuarios, {
            nome: form.nome,
            email: form.email,
            perfil,
            [fkField]: entityId,
            ...(entityType === 'municipio' && user?.estadoId ? { estadoId: user.estadoId } : {}),
            ...(user?.redeId ? { redeId: user.redeId } : {}),
            ...(form.telefone ? { telefone: form.telefone } : {}),
          });
          setCredenciais({
            email: form.email,
            nome: form.nome,
            telefone: form.telefone,
            perfil,
            activationLink: resp.activationLink,
            whatsappLink: resp.whatsappLink,
          });
          toast.success(resp.message || 'Convite enviado!');
        }
      }
      fetchUsers();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Erro ao salvar';
      const lower = errorMsg.toLowerCase();
      if (lower.includes('cpf') || lower.includes('e-mail') || lower.includes('email') || lower.includes('já existe') || lower.includes('já está cadastrado') || e?.response?.status === 409 || e?.response?.status === 400) {
        toast.warning(`Conflito de Dados: ${errorMsg}`);
      } else {
        toast.error(errorMsg, e);
      }
    } finally { setSaving(false); }
  };

  /* -- DEACTIVATE --------------------------------------------- */
  const requestDeactivate = (u: ManagedUser) => setDeactivateConfirm(u);
  const confirmDeactivate = async () => {
    if (!deactivateConfirm) return;
    const u = deactivateConfirm;
    setDeactivateConfirm(null);
    try {
      if (entityType === 'escola') {
        // Suspender conta: mantém visível na lista, bloqueia acesso
        await api.patch(ROUTES.equipe.status(entityId, u.id), { statusConta: 'SUSPENSA' });
      } else {
        await api.patch(`${ROUTES.admin.usuarios}/${u.id}/toggle`);
      }
      toast.success('Usuário desativado');
      fetchUsers();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao desativar'); }
  };

  const handleActivateUser = async (u: ManagedUser) => {
    try {
      let respData: any = null;
      if (entityType === 'escola') {
        const { data } = await api.patch(ROUTES.equipe.status(entityId, u.id), { statusConta: 'ATIVA' });
        respData = data;
      } else {
        const { data } = await api.patch(`${ROUTES.admin.usuarios}/${u.id}/toggle`);
        respData = data;
      }

      if (respData?.statusConta === 'PENDENTE' || respData?.reconvidado || !respData?.ativadoEm) {
        toast.success('Usuário reativado! Novo link de ativação gerado.');
        const actLink = respData?.activationLink || (respData?.tokenAtivacao ? normalizeActivationLink(respData.tokenAtivacao) : '');
        if (actLink) {
          setCredenciais({
            nome: u.nome,
            email: u.email,
            perfil: (u as any).perfil || label,
            telefone: (u as any).telefone,
            activationLink: actLink,
            whatsappLink: (u as any).telefone ? `https://wa.me/${(u as any).telefone.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá, ${u.nome}! Sua conta foi reativada. Acesse para definir sua senha: ${actLink}`)}` : null,
          });
        }
      } else {
        toast.success('Usuário reativado com sucesso!');
      }

      fetchUsers();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao ativar');
    }
  };

  const handleHardDeleteUser = (u: ManagedUser) => {
    setUserToDelete(u);
  };

  const confirmHardDelete = async () => {
    if (!userToDelete) return;
    const u = userToDelete;
    setUserToDelete(null);
    try {
      if (entityType === 'escola') {
        await api.delete(ROUTES.equipe.findOne(entityId, u.id));
      } else {
        await api.delete(`${ROUTES.admin.usuarios}/${u.id}`);
      }
      toast.success(entityType === 'escola' ? 'Membro removido da lista!' : 'Usuário removido definitivamente!');
      fetchUsers();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao remover usuário');
    }
  };

  /* -- RESEND INVITE ------------------------------------------ */
  const handleResend = async (userId: string) => {
    setResending(true);
    try {
      const { data } = await api.post(`${ROUTES.admin.usuarios}/${userId}/resend`);
      const link = data.activationLink || (data.user?.tokenAtivacao ? `${window.location.origin}/ativar?token=${data.user.tokenAtivacao}` : '');
      if (link) {
        setNewActivationLink(link);
        const targetUser = users.find(u => u.id === userId);
        setCredenciais({
          email: targetUser?.email || '',
          nome: targetUser?.nome || '',
          telefone: targetUser?.telefone || '',
          perfil: targetUser?.perfil || '',
          activationLink: link,
          whatsappLink: data.whatsappLink || null,
        });
      }
      toast.success(data.message || 'Novo convite gerado com sucesso!');
      fetchUsers();
      // Refresh editing user data
      if (editingUser) {
        const updated = users.find(u => u.id === userId);
        if (updated) setEditingUser({ ...updated, tokenAtivacao: data.user?.tokenAtivacao, tokenExpiraEm: data.user?.tokenExpiraEm, statusConta: 'PENDENTE' });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error?.message || 'Erro ao gerar novo link', e);
    } finally { setResending(false); }
  };

  /* -- COPY --------------------------------------------------- */
  const copiarCredenciais = () => {
    if (!credenciais) return;
    copyCredentials(credenciais.email, credenciais.senha, credenciais.activationLink);
  };

  /* -- RENDER ------------------------------------------------- */

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Carregando...</div>;
  }

  // -- Invite Result Dialog (shown after creation)
  if (credenciais && !editingUser && credenciais.activationLink) {
    return (
      <>
        <InviteResultDialog
          open={true}
          nome={credenciais.nome || credenciais.email}
          email={credenciais.email}
          perfil={credenciais.perfil}
          telefone={credenciais.telefone}
          activationLink={credenciais.activationLink}
          whatsappLink={credenciais.whatsappLink}
          onClose={() => { setCredenciais(null); setShowForm(false); }}
        />
      </>
    );
  }

  // -- Credentials display (password mode for escola)
  if (credenciais && !editingUser && credenciais.senha) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #34d399)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', margin: '0 auto 12px' }}>
          <User size={26} />
        </div>
        <h4 style={{ margin: '0 0 4px', fontWeight: 700 }}>Usuário criado!</h4>
        <p style={{ color: '#6b7280', fontSize: '.85rem', marginBottom: 16 }}>Repasse as credenciais:</p>
        <div style={{ background: 'var(--bg-muted, #f1f4f8)', borderRadius: 10, padding: 16, textAlign: 'left', maxWidth: 400, margin: '0 auto', border: '1px solid var(--border, #e5e7eb)' }}>
          <div style={{ marginBottom: 10 }}>
            <span style={{ fontSize: '.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Email</span>
            <div style={{ fontWeight: 700, fontSize: '.95rem' }}>{credenciais.email}</div>
          </div>
          <div>
            <span style={{ fontSize: '.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Senha</span>
            <div style={{ fontWeight: 700, fontSize: '.95rem', color: '#6366f1', fontFamily: 'monospace' }}>{credenciais.senha}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
          <button className="btn-cancel" onClick={copiarCredenciais} style={{ fontSize: '.8rem' }}>Copiar</button>
          <button className="btn-save" onClick={() => { setCredenciais(null); setShowForm(false); }} style={{ fontSize: '.8rem' }}>OK</button>
        </div>
      </div>
    );
  }

  // -- New/Edit form
  if (showForm) {

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: '.95rem' }}>
            {editingUser ? `Editar: ${editingUser.nome}` : `Convidar ${cargoLabel}`}
          </h4>
        </div>

        {/* Painel de Status do Convite (somente edicao) */}
        {editingUser && (
          <InviteStatusCard
            user={editingUser as InviteUser}
            onResend={handleResend}
            resending={resending}
            newActivationLink={newActivationLink}
          />
        )}

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Nome *</label>
          <input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="email@exemplo.com" disabled={!!editingUser} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Telefone</label>
            <input className="form-input" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} placeholder="(31) 99999-0000" />
          </div>
        </div>

        {/* Info: todos os perfis agora usam link de ativação */}
        {!editingUser && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.15)', fontSize: '.78rem', color: '#6b7280' }}>
            <Link size={12} style={{ marginRight: 4, verticalAlign: 'middle', color: '#6366f1' }} />
            O membro receberá um <strong>link de ativação</strong> para definir a própria senha.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="btn-cancel" onClick={() => { setShowForm(false); setCredenciais(null); }}>Cancelar</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    );
  }

  // -- User list
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: '.85rem', color: '#6b7280' }}>
          {users.length} {users.length === 1 ? 'usuario' : 'usuarios'}
        </span>
        <button className="btn-primary" onClick={openNew} style={{ padding: '6px 14px', fontSize: '.8rem' }}>
          <><UserPlus size={14} /> Convidar {cargoLabel}</>
        </button>
      </div>

      {users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9ca3af' }}>
          <User size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: '.85rem' }}>Nenhum {label.toLowerCase().replace(/s$/, '')} cadastrado</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sortedUsers.map(u => {
            const info = getInviteStatus(u as InviteUser);
            const isDesativado = info.status === 'suspenso' || u.ativo === false || (u as any).statusConta === 'SUSPENSA' || (u as any).statusConta === 'BLOQUEADA';

            let badgeBg = 'rgba(239, 68, 68, 0.12)';
            let badgeColor = '#ef4444';
            let badgeBorder = 'rgba(239, 68, 68, 0.25)';
            let badgeText = 'DESATIVADO';

            if (isDesativado) {
              badgeBg = 'rgba(239, 68, 68, 0.12)';
              badgeColor = '#ef4444';
              badgeBorder = 'rgba(239, 68, 68, 0.25)';
              badgeText = 'DESATIVADO';
            } else if (info.status === 'ativo') {
              badgeBg = 'rgba(16, 185, 129, 0.12)';
              badgeColor = '#10b981';
              badgeBorder = 'rgba(16, 185, 129, 0.25)';
              badgeText = 'ATIVO';
            } else if (info.status === 'expirado') {
              badgeBg = 'rgba(239, 68, 68, 0.12)';
              badgeColor = '#ef4444';
              badgeBorder = 'rgba(239, 68, 68, 0.25)';
              badgeText = 'LINK EXPIRADO';
            } else if (info.status === 'pendente' || info.status === 'ativando') {
              badgeBg = 'rgba(245, 158, 11, 0.12)';
              badgeColor = '#d97706';
              badgeBorder = 'rgba(245, 158, 11, 0.25)';
              badgeText = 'AGUARDANDO';
            }

            return (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
                transition: 'background .15s',
              }}>
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: u.fotoUrl ? `url(${u.fotoUrl}) center/cover` : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '.8rem',
                }}>
                  {!u.fotoUrl && (u.nome?.charAt(0)?.toUpperCase() || '?')}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.nome}
                  </div>
                  <div style={{ fontSize: '.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email}
                  </div>
                </div>

                {/* Status badge */}
                <span style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: '.64rem', fontWeight: 700,
                  background: badgeBg, color: badgeColor, border: `1px solid ${badgeBorder}`,
                  textTransform: 'uppercase', letterSpacing: '.03em',
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3,
                }}>
                  {badgeText}
                </span>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button className="action-btn" onClick={() => openEdit(u)} title="Editar Usuário"><Pencil size={14} /></button>

                  {/* Reenviar se pendente ou expirado e NÃO desativado */}
                  {!isDesativado && (info.status === 'pendente' || info.status === 'expirado') && (
                    <button
                      className="action-btn"
                      style={{ color: '#d97706' }}
                      onClick={() => handleResend(u.id)}
                      disabled={resending}
                      title="Reenviar / Gerar Novo Link de Ativação de 72h"
                    >
                      {resending ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                    </button>
                  )}

                  {isDesativado ? (
                    <button
                      className="action-btn"
                      style={{ color: '#10b981' }}
                      onClick={() => handleActivateUser(u)}
                      title="Reativar Acesso do Usuário"
                    >
                      <UserCheck size={14} />
                    </button>
                  ) : (
                    <button
                      className="action-btn action-btn-danger"
                      onClick={() => requestDeactivate(u)}
                      title="Desativar / Bloquear Acesso"
                    >
                      <UserX size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!deactivateConfirm}
        title="Desativar usuário"
        message={deactivateConfirm ? `Desativar "${deactivateConfirm.nome}"?${entityType === 'escola' ? ' O membro perderá acesso à escola.' : ''}` : ''}
        confirmLabel="Desativar"
        onCancel={() => setDeactivateConfirm(null)}
        onConfirm={() => void confirmDeactivate()}
      />

      {/* Modal Completo de Edição de Usuário (Dados, Permissões, Senha) */}
      {selectedUserForModal && (
        <UsuarioEditModal
          open={!!selectedUserForModal}
          userId={selectedUserForModal.id}
          initialData={selectedUserForModal}
          onClose={() => setSelectedUserForModal(null)}
          onUpdated={fetchUsers}
        />
      )}

      {/* Modal de Confirmação com Senha de Segurança para Remoção */}
      <PasswordConfirmModal
        open={!!userToDelete}
        title="Remover Usuário da Lista"
        description={`Para remover permanentemente "${userToDelete?.nome}" da lista, digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Remover Usuário"
        onCancel={() => setUserToDelete(null)}
        onConfirm={confirmHardDelete}
      />
    </div>
  );
}
